import { Box, Typography, Dialog, DialogContent } from '@mui/material';
import { useState } from 'react';
import { ReviewQueueGrid, DraftReviewCard } from '../../components/clinician';
import draftsData from '../../mocks/drafts.json';

interface EvidenceRef {
  refType: "CGM_EVENT" | "MEAL_ENTRY" | "CONTEXT_NOTE" | "LAB_RESULT" | "PROTOCOL_VERSION" | "MESSAGE_RESPONSE";
  refId: string;
  label?: string;
  meta?: Record<string, unknown>;
}

type MessageDraft = {
  id: string;
  patientId: string;
  patientName: string;
  draftContent: string;
  rationale?: string;
  evidenceRefs: EvidenceRef[];
  status: "PENDING_REVIEW" | "APPROVED" | "REJECTED";
  createdAt: string;
};

interface StoredDraft {
  id: string;
  patientId: string;
  content: string;
  reasoning: string;
  evidenceRefs: {
    refType: string;
    refId: string;
    label?: string;
  }[];
  status: string;
  generatedAt: string;
  patient: {
    firstName: string;
    lastName: string;
  };
}

export const DraftReviewPage = () => {
  // Load drafts from both mock data and localStorage (AI-generated from patient questions)
  const loadAllDrafts = (): StoredDraft[] => {
    const mockDrafts = [...draftsData] as StoredDraft[];
    const aiDrafts = JSON.parse(localStorage.getItem('aiDrafts') || '[]') as StoredDraft[];
    return [...aiDrafts, ...mockDrafts];
  };

  // Adapt drafts data to match MessageDraft interface
  const [drafts] = useState<MessageDraft[]>(() =>
    loadAllDrafts().map((draft: StoredDraft) => ({
      id: draft.id,
      patientId: draft.patientId,
      patientName: `${draft.patient.firstName} ${draft.patient.lastName}`,
      draftContent: draft.content,
      rationale: draft.reasoning,
      evidenceRefs: draft.evidenceRefs.map((ref: { refType: string; refId: string; label?: string }) => ({
        refType: ref.refType as "CGM_EVENT" | "MEAL_ENTRY" | "CONTEXT_NOTE" | "LAB_RESULT" | "PROTOCOL_VERSION" | "MESSAGE_RESPONSE",
        refId: ref.refId,
        label: ref.label
      })),
      status: draft.status as "PENDING_REVIEW" | "APPROVED" | "REJECTED",
      createdAt: draft.generatedAt
    }))
  );
  const [selectedDraft, setSelectedDraft] = useState<MessageDraft | null>(null);

  const handleDraftClick = (draft: MessageDraft) => {
    setSelectedDraft(draft);
  };

  const handleApprove = async (decision: string, editedContent?: string) => {
    // TODO: Connect to API
    console.log('Draft decision:', decision, editedContent);
    await new Promise(resolve => setTimeout(resolve, 500));
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Review AI Drafts
      </Typography>
      <Typography color="text.secondary" paragraph>
        Review and approve AI-generated patient messages.
      </Typography>

      <ReviewQueueGrid
        drafts={drafts}
        onDraftClick={handleDraftClick}
      />

      <Dialog
        open={!!selectedDraft}
        onClose={() => setSelectedDraft(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {selectedDraft && (
            <DraftReviewCard
              draft={selectedDraft}
              onApprove={handleApprove}
              onClose={() => setSelectedDraft(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
